import { clone, cond, either, mergeDeepRight } from 'ramda'
import AxiosWrap from './Axios'
import { CreateAxiosOptions } from './axiosTransform'
import { ErrorMessageMode, RequestOptions, Result } from './modal'
import { AxiosTransform } from './axiosTransform'
import type { AxiosResponse, AxiosRequestConfig } from 'axios'
import { ContentType, HttpMethod, ServerResultCode } from '@/enums/http'
import { isString } from '@/utils/is'
import packageJson from '../../../package.json'
import qs from 'qs'
import { getAuthCache } from '../auth'
import { ElMessageBox, ElMessage } from 'element-plus'
import { OUTER_TOKEN_KEY, TOKEN_KEY } from '@/enums/cache'
import { useUserStoreWithoutSetup } from '@/store/modules/user'
import { toOuterLogin, isReferrerFromAuthBackup } from '../login'
import { BOOLEAN } from '../constants'
import '#/global.d'
import { isDev } from '..'

type ErrorCode = string | number

function handleErrorCode(errorCode: ErrorCode) {
  if (either(isNoPermission, isOverDue)(errorCode)) {
    handleReLogin()
    return true
  }

  const match = cond([
    [isNotLogin, toLogin],
    [isUpgrade, toUpgrade]
  ])(errorCode)

  return match
}

function isNotLogin(code: ErrorCode) {
  return code === ServerResultCode.NOT_LOGIN
}

function isNoPermission(code: ErrorCode) {
  return code === ServerResultCode.NO_PERMISSION
}

function isOverDue(code: ErrorCode) {
  return code === ServerResultCode.OVERDUE
}

function isUpgrade(code: ErrorCode) {
  return code === ServerResultCode.UPGRADE
}

function toLogin() {
  toOuterLogin()
}

async function handleReLogin() {
  const userStore = useUserStoreWithoutSetup()
  await userStore.logout()
  return toLogin()
}

function toUpgrade() {
  ElMessage.error('网站功能升级，请刷新页面获取最新功能')
  setTimeout(() => {
    window.location.reload()
  }, 5000)
  return true
}

/**
 * @description 获取时间戳
 * @returns 时间戳(秒)
 */
function getTimestamp() {
  return String(Math.floor(Date.now() / 1000))
}

const { lastBuildTime } = __APP_INFO__
function getDefaultParams() {
  return {
    appVersion: packageJson.version,
    lastBuildTime,
    platform: 'web',
    timestamp: getTimestamp()
  }
}

function showErrorMsg(msg: string, mode: ErrorMessageMode) {
  if (mode === 'none') {
    return true
  }
  if (mode === 'message') {
    ElMessage.error(msg)
    return true
  }
  if (mode === 'modal') {
    ElMessageBox.alert(msg, '错误提示')
    return true
  }
}

/**
 * 处理 method 和 content-type 格式
 */
function transformContent(config: AxiosRequestConfig) {
  const headers = config.headers
  const contentType = headers?.['Content-Type'] || headers?.['content-type']

  const params = config.params || {}
  const data = { ...params, ...config.data } || false
  if (config.method?.toUpperCase() === HttpMethod.GET) {
    return config
  }

  if (contentType === ContentType.FORM_URLENCODED) {
    return {
      ...config,
      data: qs.stringify(data, { arrayFormat: 'brackets' })
    }
  }

  if (contentType === ContentType.FORM_DATA) {
    const formData = new FormData()
    Object.keys(data ?? params).forEach((key) => {
      formData.append(key, data[key])
    })

    return {
      ...config,
      data: formData
    }
  }

  if (contentType === ContentType.JSON) {
    config.data = config.data ?? config.params
  }

  return config
}

function isLeyoApi(url: string) {
  return ['scw-leyo'].includes(url)
}

function getLeyoApiBaseUrl() {
  if (isDev()) {
    return 'https://pms.leyodev.cn'
  } else if (import.meta.env.VITE_BUILD_ENV === 'canary') {
    return 'https://pms2.leyopharm.com'
  } else {
    return 'https://pms.leyopharm.com'
  }
}

function isGysWebApi(url: string) {
  return ['gys-web'].includes(url)
}

function getGysWebApiBaseUrl() {
  if (isDev()) {
    return 'https://gys.leyodev.cn'
  } else {
    return 'https://gys.leyopharm.com'
  }
}

const transform: AxiosTransform = {
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinPrefix, urlPrefix } = options

    if (joinPrefix && urlPrefix) {
      config.url = `${urlPrefix}${config.url}`
    }

    if (apiUrl && isString(apiUrl)) {
      config.url = `${apiUrl}${config.url}`
    }

    if (isLeyoApi(options.apiUrl || '') || isGysWebApi(options.apiUrl || '')) {
      config.params = {
        ...(config.params || {})
      }
    } else {
      config.params = {
        ...(config.params || {}),
        ...getDefaultParams()
      }
    }

    if (options.isSendAuth8Params) {
      const beAuth8 = isReferrerFromAuthBackup() ? BOOLEAN.YES : BOOLEAN.NO
      if (config.method?.toUpperCase() === HttpMethod.GET && config.params) {
        config.params.beAuth8 = beAuth8
      }
      if (config.method?.toUpperCase() === HttpMethod.POST && config.data) {
        config.data.beAuth8 = beAuth8
      }
    }

    if (isLeyoApi(options.apiUrl || '')) {
      config.baseURL = getLeyoApiBaseUrl()
    }

    if (isGysWebApi(options.apiUrl || '')) {
      config.baseURL = getGysWebApiBaseUrl()
    }

    return config
  },

  requestInterceptors: (config) => {
    config.headers = {
      ...(config.headers || {}),
      token: getAuthCache(TOKEN_KEY)
    }
    const { useAuthorization } = config.requestOptions || {}
    if (useAuthorization) {
      config.headers['Authorization'] = `Bearer ${getAuthCache(OUTER_TOKEN_KEY)}`
    }
    config = transformContent(config)
    return config
  },

  transformRequestHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    const { isTransformResponse, isReturnNativeResponse, ignoreError = false } = options
    const { data } = res
    const { code, data: result, message } = data ?? {}
    if (!ignoreError) {
      const handleRes = handleErrorCode(code)
      if (handleRes) {
        throw new Error(message)
      }
    }

    if (isReturnNativeResponse) {
      return res
    }

    if (!isTransformResponse) {
      return res.data
    }

    const hasSuccess =
      Reflect.has(data, 'code') && code && String(code) === ServerResultCode.SUCCESS
    if (hasSuccess) {
      options.defaultSuccessMsg && ElMessage.success(message)
      return result
    }
    const errorMessageMode = options.errorMessageMode ?? 'message'
    showErrorMsg(message, errorMessageMode)

    throw new Error(message ?? 'api request error')
  },

  responseInterceptors: (res: AxiosResponse<unknown>) => {
    return res
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responseInterceptorsCatch: (error: any) => {
    const { response, message, config } = error || {}
    const errorMessageMode = config?.requestOptions?.errorMessageMode ?? 'none'
    const msg: string = response?.data?.message ?? message ?? ''

    showErrorMsg(msg, errorMessageMode)

    return Promise.reject(error)
  }
}

function createAxios(opt?: Partial<CreateAxiosOptions>) {
  return new AxiosWrap(
    mergeDeepRight(
      {
        timeout: 120 * 1000,
        headers: {
          'Content-Type': ContentType.JSON
        },
        transform: clone(transform),
        requestOptions: {
          joinPrefix: false,
          urlPrefix: '',
          isReturnNativeResponse: false,
          isTransformResponse: true,
          isSendAuth8Params: false,
          errorMessageMode: 'message',
          apiUrl: '/scw-main',
          defaultSuccessMsg: false
        }
      },
      opt || {}
    )
  )
}

const request = createAxios()

export default request
